import React from 'react';
import { css, SerializedStyles } from '@emotion/react';
import { token } from '@atlaskit/tokens';
import { ButtonAppearance, ButtonSize } from '../index';
import {
  learningButtonStyles,
  learningButtonFontStyles,
  learningButtonSpacingStyles,
  learningButtonResponsiveSpacing,
  learningButtonResponsiveFontStyles,
  loadingStyles,
} from '../styles/sharedButtonStyles';

export const getBasicStyles = (appearance: ButtonAppearance, size: ButtonSize) => [
  learningButtonStyles['basic'],
  learningButtonStyles[appearance],
  learningButtonFontStyles[size],
];

export const getIconSpacingStyles = (
  size: ButtonSize,
  isButtonWithoutIcon: boolean,
  showBeforeIcon: React.ReactElement | false,
  showAfterIcon: React.ReactElement | false,
) => {
  const styles = [];

  if (isButtonWithoutIcon) {
    styles.push(learningButtonSpacingStyles[size]);
  }

  if (showBeforeIcon) {
    styles.push(learningButtonSpacingStyles[`${size}-icon-before`]);
  }

  if (showAfterIcon) {
    styles.push(learningButtonSpacingStyles[`${size}-icon-after`]);
  }

  return styles;
};

export const getResponsiveStyles = (
  isResponsiveSpacing: boolean,
  size: ButtonSize,
  showBeforeIcon: React.ReactElement | false,
  showAfterIcon: React.ReactElement | false,
) => {
  const styles = [learningButtonResponsiveSpacing[size], learningButtonResponsiveFontStyles];

  if (!isResponsiveSpacing) {
    return [];
  }

  if (showBeforeIcon) {
    styles.push(learningButtonResponsiveSpacing[`${size}-icon-before`]);
  }

  if (showAfterIcon) {
    styles.push(learningButtonResponsiveSpacing[`${size}-icon-after`]);
  }

  return styles;
};

export const getAppearanceStyles = (appearance: ButtonAppearance) => {
  if (appearance === ButtonAppearance.PRIMARY) {
    return css({ color: token('color.text.inverse') });
  }

  if (appearance === ButtonAppearance.SECONDARY) {
    return css({ color: token('color.text.brand') });
  }

  return null;
};

export const getStateStyles = (selected: boolean, isLoading: boolean, disabled: boolean, fullWidth: boolean) => {
  const styles = [];

  if (selected) {
    styles.push(learningButtonStyles['selected']);
  }

  if (isLoading || disabled) {
    styles.push(
      css({
        ...loadingStyles,
        pointerEvents: 'none',
        cursor: 'not-allowed',
      }),
    );
  }

  if (fullWidth) {
    styles.push(
      css({
        width: '100%',
        maxWidth: 'none',
        justifyContent: 'center',
      }),
    );
  }

  return styles;
};

export const getLinkButtonSpacingStyles = (
  size: ButtonSize,
  isButtonWithoutIcon: boolean,
  showBeforeIcon: React.ReactElement | false,
  showAfterIcon: React.ReactElement | false,
  withPadding: boolean = true,
) => {
  const linkButtonSpacingStyles = {
    medium: css({
      padding: `10px ${token('space.150')}`,
    }),
    'medium-icon-before': css({
      padding: `10px ${token('space.150')} 10px 10px`,
    }),
    'medium-icon-after': css({
      padding: `10px 10px 10px ${token('space.150')}`,
    }),
    large: css({
      maxWidth: 'max-content',
      padding: `${token('space.150')} ${token('space.200')}`,
    }),
    'large-icon-before': css({
      padding: `${token('space.150')} ${token('space.200')} ${token('space.150')} ${token('space.150')}`,
    }),
    'large-icon-after': css({
      padding: `${token('space.150')} ${token('space.150')} ${token('space.150')} ${token('space.200')}`,
    }),
  } as { [key: string]: SerializedStyles };

  const styles = [];

  if (isButtonWithoutIcon) {
    styles.push(linkButtonSpacingStyles[size]);
  }

  if (showBeforeIcon) {
    styles.push(linkButtonSpacingStyles[`${size}-icon-before`]);
  }

  if (showAfterIcon) {
    styles.push(linkButtonSpacingStyles[`${size}-icon-after`]);
  }

  if (!withPadding) {
    styles.push(css({ padding: 0 }));
  }

  return styles;
};
